import React, { useEffect, useState, useMemo } from 'react';
import { transactionApi, watchlistApi } from '../services/api';

const fmt = (n: number) => n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtInt = (n: number) => n.toLocaleString('en-US');

/* ---------- Interfaces ---------- */
interface SummaryItem {
  companyName: string; ticker: string; exchange: string;
  currentPrice: number; change: number; changePercent: number;
  shares: number; costBasis: number; marketValue: number;
  capGain: number; capGainPercent: number; daysGain: number;
  overallReturn: number; averageCost: number;
}
interface SummaryData {
  items: SummaryItem[];
  totalCostBasis: number; totalMarketValue: number;
  totalCapGain: number; totalCapGainPercent: number;
  totalDaysGain: number; totalOverallReturn: number;
}

interface VolumeMover {
  ticker: string; exchange: string;
  avgVolume: number; todayVolume: number; ratio: number;
}

interface WatchItem {
  id: number; listID: string; ticker: string; exchange: string;
  name: string | null; startDate: string | null;
  price: number | null; comments: string | null;
  currentPrice: number | null; changePercent: number | null;
}

/* ---------- Constants ---------- */
type TabKey = 'dailySummary' | 'volumeMovers' | 'watchlist';

const TABS: { key: TabKey; label: string }[] = [
  { key: 'dailySummary', label: 'Daily Summary' },
  { key: 'volumeMovers', label: 'Volume Movers' },
  { key: 'watchlist', label: 'Watch Lists' },
];

/* ---------- Sub-components ---------- */
type SummarySortKey = 'companyName' | 'ticker' | 'currentPrice' | 'change' | 'changePercent' | 'shares' | 'averageCost' | 'costBasis' | 'marketValue' | 'capGain' | 'capGainPercent' | 'daysGain' | 'overallReturn';

const summaryYahooUrl = (ticker: string, exchange: string) => {
  const suffix = exchange === 'TSE' ? '.TO' : exchange === 'CVE' ? '.V' : exchange === 'CSE' ? '.CN' : '';
  return `https://finance.yahoo.com/quote/${ticker}${suffix}/`;
};

const DailySummaryTab: React.FC = () => {
  const [data, setData] = useState<SummaryData | null>(null);
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState<SummarySortKey>('changePercent');
  const [sortAsc, setSortAsc] = useState(false);

  useEffect(() => {
    const fetch = () => {
      transactionApi.dailySummary()
        .then((res) => setData(res.data))
        .catch(() => {})
        .finally(() => setLoading(false));
    };
    fetch();
    const interval = setInterval(fetch, 60000);
    return () => clearInterval(interval);
  }, []);

  const color = (val: number) => val >= 0 ? 'green' : 'red';

  const handleSort = (key: SummarySortKey) => {
    if (sortKey === key) setSortAsc(!sortAsc);
    else { setSortKey(key); setSortAsc(key === 'companyName' || key === 'ticker'); }
  };

  const sortArrow = (key: SummarySortKey) => sortKey === key ? (sortAsc ? ' \u25B2' : ' \u25BC') : '';

  const sorted = useMemo(() => {
    if (!data) return [];
    const arr = [...data.items];
    const dir = sortAsc ? 1 : -1;
    arr.sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (av < bv) return -dir;
      if (av > bv) return dir;
      return 0;
    });
    return arr;
  }, [data, sortKey, sortAsc]);

  if (loading) return <div style={{ textAlign: 'center', padding: '2em' }}>Loading Daily Summary...</div>;
  if (!data) return <div style={{ textAlign: 'center', padding: '2em' }}>Failed to load data</div>;

  const thSort = (key: SummarySortKey, label: string) => (
    <th style={{ ...styles.th, cursor: 'pointer', userSelect: 'none' }} onClick={() => handleSort(key)}>{label}{sortArrow(key)}</th>
  );

  return (
    <table style={styles.table}>
      <thead>
        <tr>
          {thSort('companyName', 'Company')}
          {thSort('ticker', 'Ticker')}
          {thSort('currentPrice', 'Price')}
          {thSort('change', 'Change')}
          {thSort('changePercent', 'Change%')}
          {thSort('shares', 'Shares')}
          {thSort('averageCost', 'Avg Cost')}
          {thSort('costBasis', 'Cost Basis')}
          {thSort('marketValue', 'Mkt Value')}
          {thSort('capGain', 'Cap Gain')}
          {thSort('capGainPercent', 'Gain%')}
          {thSort('daysGain', "Day's Gain")}
          {thSort('overallReturn', 'Return%')}
        </tr>
      </thead>
      <tbody>
        {sorted.map((item, idx) => {
          const rowBg = idx % 2 === 0 ? '#fff' : '#e6f2ff';
          return (
            <tr key={item.ticker} style={{ backgroundColor: rowBg }}>
              <td style={styles.td}>{item.companyName}</td>
              <td style={styles.td}>
                <a href={summaryYahooUrl(item.ticker, item.exchange)} target="_blank" rel="noreferrer"
                  style={{ fontWeight: 'bold', color: '#0069cc', textDecoration: 'none' }}>{item.exchange}:{item.ticker}</a>
              </td>
              <td style={styles.tdR}>{item.currentPrice.toFixed(2)}</td>
              <td style={{ ...styles.tdR, color: color(item.change) }}>{item.change.toFixed(2)}</td>
              <td style={{ ...styles.tdR, color: color(item.changePercent) }}>{item.changePercent.toFixed(2)}%</td>
              <td style={styles.tdR}>{fmtInt(item.shares)}</td>
              <td style={styles.tdR}>{item.averageCost.toFixed(3)}</td>
              <td style={styles.tdR}>{fmt(item.costBasis)}</td>
              <td style={styles.tdR}>{fmt(item.marketValue)}</td>
              <td style={{ ...styles.tdR, color: color(item.capGain) }}>{fmt(item.capGain)}</td>
              <td style={{ ...styles.tdR, color: color(item.capGainPercent) }}>{item.capGainPercent.toFixed(2)}%</td>
              <td style={{ ...styles.tdR, color: color(item.daysGain) }}>{fmt(item.daysGain)}</td>
              <td style={{ ...styles.tdR, color: color(item.overallReturn) }}>{item.overallReturn.toFixed(2)}%</td>
            </tr>
          );
        })}
      </tbody>
      <tfoot>
        <tr style={{ backgroundColor: '#cce6ff', fontWeight: 'bold' }}>
          <td style={styles.td} colSpan={7}>Portfolio Total ({data.items.length} positions)</td>
          <td style={styles.tdR}>{fmt(data.totalCostBasis)}</td>
          <td style={styles.tdR}>{fmt(data.totalMarketValue)}</td>
          <td style={{ ...styles.tdR, color: color(data.totalCapGain) }}>{fmt(data.totalCapGain)}</td>
          <td style={{ ...styles.tdR, color: color(data.totalCapGainPercent) }}>{data.totalCapGainPercent.toFixed(2)}%</td>
          <td style={{ ...styles.tdR, color: color(data.totalDaysGain) }}>{fmt(data.totalDaysGain)}</td>
          <td style={{ ...styles.tdR, color: color(data.totalOverallReturn) }}>{data.totalOverallReturn.toFixed(2)}%</td>
        </tr>
      </tfoot>
    </table>
  );
};

const VolumeMoversTab: React.FC = () => {
  const [items, setItems] = useState<VolumeMover[]>([]);
  const [tradeDate, setTradeDate] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    transactionApi.volumeMovers()
      .then((res) => { setItems(res.data.items); setTradeDate(res.data.date); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div style={{ textAlign: 'center', padding: '2em' }}>Loading Volume Movers...</div>;

  return (
    <>
      <p style={{ textAlign: 'center', fontSize: '0.8em', color: '#666', margin: '0.5em 0' }}>
        Stocks trading at 2x+ their average volume ({tradeDate})
      </p>
      <table style={{ ...styles.table, width: '60%', margin: '0 auto' }}>
        <thead>
          <tr>
            <th style={styles.th}>#</th>
            <th style={styles.th}>Ticker</th>
            <th style={styles.th}>Avg Volume</th>
            <th style={styles.th}>Today Volume</th>
            <th style={styles.th}>Ratio</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, idx) => {
            const rowBg = idx % 2 === 0 ? '#fff' : '#e6f2ff';
            return (
              <tr key={`${item.exchange}:${item.ticker}`} style={{ backgroundColor: rowBg }}>
                <td style={{ ...styles.td, textAlign: 'center' }}>{idx + 1}</td>
                <td style={styles.td}><strong>{item.exchange}:{item.ticker}</strong></td>
                <td style={{ ...styles.td, textAlign: 'right' }}>{Math.round(item.avgVolume).toLocaleString()}</td>
                <td style={{ ...styles.td, textAlign: 'right' }}>{item.todayVolume.toLocaleString()}</td>
                <td style={{ ...styles.td, textAlign: 'right', color: '#0069cc', fontWeight: 'bold' }}>{item.ratio.toFixed(1)}x</td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {items.length === 0 && <p style={{ textAlign: 'center', padding: '2em', color: '#999' }}>No volume movers found for today</p>}
    </>
  );
};

const WatchListTab: React.FC = () => {
  const [items, setItems] = useState<WatchItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ listID: '', ticker: '', exchange: '', name: '', price: 0, comments: '' });

  const loadData = () => {
    setLoading(true);
    watchlistApi.list()
      .then((res) => setItems(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadData(); }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await watchlistApi.create({ ...form, startDate: new Date(new Date().toLocaleString('en-US', { timeZone: 'America/New_York' })).toISOString().slice(0, 10) });
    setShowForm(false);
    setForm({ listID: '', ticker: '', exchange: '', name: '', price: 0, comments: '' });
    loadData();
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Remove from watchlist?')) return;
    await watchlistApi.delete(id);
    loadData();
  };

  const color = (val: number | null) => {
    if (val === null) return '#333';
    return val >= 0 ? 'green' : 'red';
  };

  return (
    <>
      <div style={{ marginBottom: '0.5em' }}>
        <input type="button" value="Add to Watch List" onClick={() => setShowForm(true)} style={styles.button} />
      </div>

      {showForm && (
        <div style={styles.formWrapper}>
          <fieldset style={styles.fieldset}>
            <legend style={styles.legend}>Add to Watch List</legend>
            <form onSubmit={handleSubmit}>
              <ol style={styles.fieldList}>
                <li style={styles.field}>
                  <label style={styles.formLabel}>List ID:</label>
                  <input value={form.listID} onChange={(e) => setForm({ ...form, listID: e.target.value })} style={styles.input} required />
                </li>
                <li style={styles.field}>
                  <label style={styles.formLabel}>Ticker:</label>
                  <input value={form.ticker} onChange={(e) => setForm({ ...form, ticker: e.target.value.toUpperCase() })} style={{ ...styles.input, width: '10em' }} required />
                </li>
                <li style={styles.field}>
                  <label style={styles.formLabel}>Exchange:</label>
                  <input value={form.exchange} onChange={(e) => setForm({ ...form, exchange: e.target.value.toUpperCase() })} style={{ ...styles.input, width: '10em' }} required />
                </li>
                <li style={styles.field}>
                  <label style={styles.formLabel}>Name:</label>
                  <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} style={{ ...styles.input, width: '20em' }} />
                </li>
                <li style={styles.field}>
                  <label style={styles.formLabel}>Watch Price:</label>
                  <input type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: parseFloat(e.target.value) || 0 })} style={{ ...styles.input, width: '10em' }} />
                </li>
                <li style={styles.field}>
                  <label style={styles.formLabel}>Comments:</label>
                  <input value={form.comments} onChange={(e) => setForm({ ...form, comments: e.target.value })} style={{ ...styles.input, width: '20em' }} />
                </li>
              </ol>
              <fieldset style={{ border: 'none', textAlign: 'right', padding: '0.3em 1em 1em' }}>
                <input type="submit" value="Add" style={styles.button} />
                <input type="button" value="Cancel" onClick={() => setShowForm(false)} style={{ ...styles.button, marginLeft: '8px' }} />
              </fieldset>
            </form>
          </fieldset>
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '2em' }}>Loading...</div>
      ) : (
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Ticker</th>
              <th style={styles.th}>Name</th>
              <th style={styles.th}>Watch Price</th>
              <th style={styles.th}>Current Price</th>
              <th style={styles.th}>Change%</th>
              <th style={styles.th}>Start Date</th>
              <th style={styles.th}>Comments</th>
              <th style={styles.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, idx) => (
              <tr key={item.id} style={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#e6f2ff' }}>
                <td style={styles.td}><strong>{item.exchange}:{item.ticker}</strong></td>
                <td style={styles.td}>{item.name || '-'}</td>
                <td style={styles.tdR}>{item.price?.toFixed(2) || '-'}</td>
                <td style={styles.tdR}>{item.currentPrice?.toFixed(2) || '-'}</td>
                <td style={{ ...styles.tdR, color: color(item.changePercent) }}>
                  {item.changePercent !== null ? `${item.changePercent.toFixed(2)}%` : '-'}
                </td>
                <td style={styles.td}>{item.startDate || '-'}</td>
                <td style={styles.td}>{item.comments || '-'}</td>
                <td style={{ ...styles.td, textAlign: 'center' }}>
                  <a href="#d" onClick={(e) => { e.preventDefault(); handleDelete(item.id); }} style={styles.actionLink}>Remove</a>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </>
  );
};

/* ---------- Main Component ---------- */
const MonitoringPage: React.FC = () => {
  const [tab, setTab] = useState<TabKey>('dailySummary');

  return (
    <div style={styles.container}>
      <div style={styles.tabs}>
        {TABS.map((t) => (
          <span
            key={t.key}
            onClick={() => setTab(t.key)}
            style={{ ...styles.tabItem, ...(tab === t.key ? styles.activeTab : {}) }}
          >
            {t.label}
          </span>
        ))}
      </div>

      {tab === 'dailySummary' && <DailySummaryTab />}
      {tab === 'volumeMovers' && <VolumeMoversTab />}
      {tab === 'watchlist' && <WatchListTab />}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: { padding: '1em 0.5em', backgroundColor: '#fff', minHeight: 'calc(100vh - 6em)' },
  tabs: { display: 'flex', gap: '0', marginBottom: '1em', borderBottom: '2px solid #0d58e6' },
  tabItem: { padding: '6px 16px', cursor: 'pointer', fontSize: '0.85em', fontWeight: 'bold', backgroundColor: '#e6f2ff', border: '1px solid #0d58e6', borderBottom: 'none', marginRight: '2px' },
  activeTab: { backgroundColor: '#cce6ff', color: '#000' },
  formWrapper: { maxWidth: '40em', margin: '1em auto' },
  fieldset: { border: '1px solid #808080', backgroundColor: '#fff', padding: '0.8em 1em 0' },
  legend: { fontWeight: 'bold', marginLeft: '0.5em', padding: '0 0.2em' },
  fieldList: { listStyle: 'none', padding: '0.8em 1em 0', margin: 0 },
  field: { paddingBottom: '0.8em', display: 'flex', alignItems: 'center' },
  formLabel: { width: '9em', textAlign: 'right' as const, marginRight: '1em' },
  input: { padding: '2px 4px', fontFamily: 'Arial, sans-serif', fontSize: '1em' },
  button: { padding: '4px 16px', backgroundColor: '#ccdcfc', color: '#000', border: '1px solid #808080', cursor: 'pointer', fontFamily: 'Arial, sans-serif', fontSize: '1em' },
  table: { width: '100%', marginTop: '0.5em', border: '2px solid #0d58e6', borderCollapse: 'collapse' as const },
  th: { border: '1px solid #0d58e6', padding: '4px 6px', backgroundColor: '#cce6ff', fontWeight: 'bold', fontSize: '0.8em', textAlign: 'center' as const },
  td: { border: '1px solid #0d58e6', padding: '3px 6px', fontSize: '0.8em' },
  tdR: { border: '1px solid #0d58e6', padding: '3px 6px', fontSize: '0.8em', textAlign: 'right' as const },
  actionLink: { color: '#0069cc', textDecoration: 'none', cursor: 'pointer', fontSize: '0.9em' },
};

export default MonitoringPage;
